/**
 * WASM engine loading and the single typed boundary around it.
 *
 * The generated bindings under `src/wasm/` are `any`-typed and excluded from
 * linting; this module is the ONLY consumer. Every export presents a precise
 * TypeScript signature, so the rest of the application never sees the raw
 * bindings and never needs an `any`.
 */

import initEngineRaw, {
  buildThreads as rawBuildThreads,
  parseMessage as rawParseMessage,
} from "../wasm/mail_wasm.js";

import type { Envelope, ParsedMessage, ThreadForest } from "./types.ts";

/**
 * Accepted initialisation inputs (mirrors the binding's `InitInput`).
 */
type EngineInput =
  RequestInfo | URL | Response | BufferSource | WebAssembly.Module;

/**
 * Singleton engine state: the in-flight (or completed) initialisation, plus
 * the readiness flag. Mutating one holder object keeps the memo without
 * reassigning module-level bindings from inside functions.
 */
const engine: { loading: Promise<void> | null; ready: boolean } = {
  loading: null,
  ready: false,
};

/**
 * Runs engine initialisation and flips the readiness flag.
 *
 * @param wasm - Optional explicit bytes or module (test hosts and offline
 * caches); when omitted, the generated glue fetches the bundled asset.
 * @returns Resolves once the engine is ready to parse.
 */
async function boot(wasm?: EngineInput): Promise<void> {
  if (wasm === undefined) {
    await initEngineRaw();
  } else {
    // The input is passed unwrapped on purpose: the glue accepts the bare
    // `InitInput` directly, and skipping its `{module_or_path}` destructure
    // keeps us independent of its `Object.prototype` identity check (which
    // breaks under module runners that evaluate files in separate realms).
    await initEngineRaw(wasm);
  }
  engine.ready = true;
}

/**
 * Keeps a failed load uncached so the next call retries.
 *
 * @param pending - The initialisation promise to track.
 * @returns A promise with the same outcome; on failure the memo is cleared.
 */
async function track(pending: Promise<void>): Promise<void> {
  try {
    await pending;
  } catch (error: unknown) {
    engine.loading = null;
    throw error;
  }
}

/**
 * Loads and initialises the WASM engine.
 *
 * Idempotent and race-safe: concurrent callers await the same load. A failed
 * load is not cached — the next call retries.
 *
 * @returns Resolves once the engine is ready to parse.
 */
export function loadEngine(): Promise<void> {
  engine.loading ??= track(boot());
  return engine.loading;
}

/**
 * Loads the engine from explicit bytes (test hosts and offline caches).
 *
 * @param wasm - Compiled module or raw `.wasm` bytes.
 * @returns Resolves once the engine is ready to parse.
 */
export async function loadEngineFromBytes(wasm: BufferSource): Promise<void> {
  engine.loading ??= track(boot({ module_or_path: wasm }));
  await engine.loading;
}

/**
 * True once the engine has been successfully initialised.
 *
 * @returns Whether `parseMessage`/`buildThreads` may be called.
 */
export function isEngineReady(): boolean {
  return engine.ready;
}

/**
 * Parses a full RFC 5322/MIME message into the renderable tree.
 *
 * @param raw - Raw message bytes as delivered by the server (never
 * pre-decoded: charset handling lives in the engine).
 * @param isHardened - Apply hostile-input limits (depth/part/header caps)
 * for untrusted sources.
 * @returns The parsed message.
 * @throws {Error} When the message is structurally unparseable, or when the
 * engine has not finished loading.
 */
export function parseMessage(
  raw: Uint8Array,
  isHardened = false,
): ParsedMessage {
  assertReady();
  return rawParseMessage(raw, isHardened) as ParsedMessage;
}

/**
 * Builds JWZ conversation threads over one page of envelopes.
 *
 * @param envelopes - Threading identity per fetched message, in fetch order;
 * every returned node's `message` field indexes this array.
 * @returns The thread forest (one root per conversation).
 * @throws {Error} When the engine has not finished loading, or when input
 * envelopes are malformed.
 */
export function buildThreads(envelopes: readonly Envelope[]): ThreadForest {
  assertReady();
  // Structured clone across the boundary; serde deserialises inside the
  // engine, so a plain array of plain objects is all that is needed.
  const plain = envelopes.map((envelope) => ({
    id: envelope.id,
    references: [...envelope.references],
  }));
  return rawBuildThreads(plain) as ThreadForest;
}

/**
 * Guards every boundary call against use before (or after a failed) load.
 *
 * @throws {Error} When the engine is not ready.
 */
function assertReady(): void {
  if (!engine.ready) {
    throw new Error(
      "WASM engine not loaded: await loadEngine() before parsing.",
    );
  }
}
